import { browserUtil } from '../../../common'
import React, { useEffect, useState } from 'react'
import { ImageUrl } from 'src/component/image/image-url'
import { ChoosePlanSkeleton } from 'src/component/profile/personal-info/choose-plan-skeleton'
import { PromoCodeField } from 'src/component/profile/promo-code-field'
import { OutlinedButton } from 'src/component/ui/button'
import { Card, CardContent } from 'src/component/ui/card'
import { Price } from 'src/dao/core-dao'
import { usePrices } from 'src/hook/use-prices'
import { coreService } from 'src/service/core-service'
import { promoCodeService, ResolvedPromoCode } from 'src/service/promo-code-service'
import { errorUtil } from 'src/util/error-util'
import { EventBusActionType, EventBusEntityType, eventBusUtil } from 'src/util/event-bus-util'
import { subscriptionUtil } from 'src/util/subscription-util'

const PROMO_INVALID_CODE = 'error.client.promo_code_invalid'

const promoAppliesTo = (promo: ResolvedPromoCode, price: Price): boolean =>
  promo.appliesToProductIds === null || promo.appliesToProductIds.includes(price.product.id)

const isPromoInvalidError = (err: any): boolean =>
  err?.message === PROMO_INVALID_CODE || err?.response?.data?.message === PROMO_INVALID_CODE

export const ChoosePlan = (): React.ReactElement => {
  const { data: prices, isLoading, error } = usePrices()
  const [promo, setPromo] = useState<ResolvedPromoCode | null>(() => promoCodeService.get())

  // Heal promos stored before the applies-to restriction was captured, so the per-plan
  // "applies / doesn't apply" state is always based on fresh Stripe data.
  useEffect(() => {
    let active = true
    promoCodeService.refresh().then((refreshed) => {
      if (active) setPromo(refreshed)
    })
    return () => {
      active = false
    }
  }, [])

  // Which plan names the promo is restricted to — shown as "Monthly plan only" on the notice.
  const scopeNote = ((): string | null => {
    if (!promo || promo.appliesToProductIds === null || !prices) return null
    const applicableNames = prices.filter((p) => promoAppliesTo(promo, p)).map((p) => p.product.name)
    if (applicableNames.length === 0 || applicableNames.length === prices.length) return null
    return `${applicableNames.join(' & ')} only`
  })()

  const removePromo = (): void => {
    promoCodeService.clear()
    setPromo(null)
  }

  const handlePlanClick = async (price: Price): Promise<void> => {
    eventBusUtil().emit({
      action: EventBusActionType.CREATE,
      entity: EventBusEntityType.CHECKOUT,
      meta: { price },
    })
    // Only send the promo to Stripe for plans it actually applies to — otherwise Stripe
    // rejects the whole checkout session ("coupon does not apply to anything in this order").
    const promoCode = promo && promoAppliesTo(promo, price) ? promo.code : undefined
    let url: string
    try {
      // Handle promo failures ourselves (silent retry below) instead of letting the global
      // interceptor toast a raw backend error code.
      url = await coreService.createCheckoutSession(price.id, promoCode, { skipErrorInterceptor: !!promoCode })
    } catch (err: any) {
      if (promoCode && isPromoInvalidError(err)) {
        removePromo()
        url = await coreService.createCheckoutSession(price.id)
      } else {
        // The promo'd request skipped the global handler, so surface the error ourselves.
        if (promoCode) errorUtil.handleHttpError(err)
        return
      }
    }
    browserUtil.redirect(url)
  }

  if (isLoading) {
    return <ChoosePlanSkeleton />
  }

  if (error) {
    return (
      <div className="p-6 bg-secondary-gray rounded-lg">
        <p className="text-light-gray font-roboto-condensed">Error loading plans. Please try again later.</p>
      </div>
    )
  }

  if (!prices || prices.length === 0) {
    return (
      <div className="p-6 bg-secondary-gray rounded-lg">
        <p className="text-light-gray font-roboto-condensed">No plans available at this time.</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <PromoCodeField promo={promo} scopeNote={scopeNote} onApplied={setPromo} onRemoved={removePromo} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {prices.map((price) => {
          const promoApplies = promo ? promoAppliesTo(promo, price) : false
          return (
            <Card key={price.id} className="bg-secondary-gray rounded-xl overflow-hidden">
              {/*
                The image height is height-relative, because the constraint is the viewport's
                *height* and Tailwind breakpoints are width-based. The card image is the dominant
                vertical cost: at a flat 410px the paywall stood ~870px tall, overflowing its
                90dvh clamp on any viewport shorter than ~970px (GH-909). Scrolling alone makes
                the buttons reachable; this makes them visible without scrolling down to a
                1440x900 laptop. Floors at the mobile 200px so a short window never squashes it
                (it scrolls instead) and ceilings at the original 410px so large displays keep
                the intended design. Kept in sync with `choose-plan-skeleton.tsx`.
              */}
              {price?.product.imageUrl && (
                <ImageUrl
                  url={price.product.imageUrl}
                  alt={price.product.name}
                  className="h-[200px] sm:h-[clamp(200px,28dvh,410px)] w-full object-cover rounded-t-[12px]"
                />
              )}
              <CardContent className="!p-5">
                <h3 className="text-light-gray font-evogria text-4xl font-normal mb-3 uppercase">{price?.product.name}</h3>
                <h4 className="text-mist text-lg font-medium font-roboto-condensed mb-2">
                  {subscriptionUtil.formatCatalogPrice(price)}
                </h4>
                {promo && (
                  <p
                    className={`font-roboto-condensed text-sm tracking-[0.5px] mb-6 sm:mb-10 ${
                      promoApplies ? 'text-secondary' : 'text-mushroom'
                    }`}
                  >
                    {promoApplies
                      ? `${promo.discountLabel ?? 'Promo'} with ${promo.code}`
                      : `Promo ${promo.code} doesn’t apply to this plan`}
                  </p>
                )}
                {!promo && <div className="mb-6 sm:mb-12" />}
                <OutlinedButton size="sm" onClick={() => handlePlanClick(price)}>
                  {price.product?.trialDurationDays === 0 ? 'START NOW' : 'FREE TRIAL'}
                </OutlinedButton>
              </CardContent>
            </Card>
          )
        })}
      </div>
    </div>
  )
}
